// M13.1 — page-level granularity toggle. Lifted out of TimeSeriesSection
// so multiple sections (TimeSeriesSection in M12.5 + SourceMixSection in
// M13.1, and future M13.x sections that need time bucketing) can share a
// single user-controlled granularity rather than each owning their own.
//
// State + smart default rule live on the parent page (ReportOverview);
// this component is purely the visual segmented control. The smart
// default — ≤30 days = day, 31-120 = week, >120 = month — lives in
// `./granularity.ts` as `inferGranularity()` so callers can compute the
// initial value off the report's bounds and feed it back through state.

import type { Granularity } from "./granularity";
export type { Granularity } from "./granularity";

export interface GranularityControlProps {
  granularity: Granularity;
  onChange: (next: Granularity) => void;
  /** Optional label rendered to the LEFT of the segmented control. */
  label?: string;
  /** Optional id for the wrapping container so the section can
   *  scroll-to / aria-labelledby it. */
  id?: string;
}

const GRANULARITIES: ReadonlyArray<Granularity> = ["day", "week", "month"];

export function GranularityControl({
  granularity,
  onChange,
  label = "Granularity",
  id,
}: GranularityControlProps) {
  return (
    <div
      id={id}
      className="inline-flex items-center gap-2"
      role="group"
      aria-label="Time bucket granularity"
    >
      <span className="text-[11px] font-bold uppercase tracking-wide text-neutral-500">
        {label}
      </span>
      <div className="inline-flex rounded-lg bg-neutral-100 p-0.5 text-xs font-semibold">
        {GRANULARITIES.map((g) => (
          <button
            key={g}
            type="button"
            onClick={() => onChange(g)}
            className={`rounded-md px-2.5 py-1 capitalize transition-colors ${
              granularity === g
                ? "bg-white text-neutral-950 shadow-sm"
                : "text-neutral-600 hover:text-neutral-900"
            }`}
            aria-pressed={granularity === g}
          >
            {g}
          </button>
        ))}
      </div>
    </div>
  );
}
